import { useMemo } from 'react';
import type { useAvailableAssetsFragment$key } from '@onramp/data/__generated__/useAvailableAssetsFragment.graphql';
import type { useGetAvailableNetworksForAssetFragment$key } from '@onramp/data/__generated__/useGetAvailableNetworksForAssetFragment.graphql';
import type { DestinationAddressAppParam } from '@onramp/shared/appParams.schema';
import bigJs from 'big.js';
import { validate as validateUuid } from 'uuid';

import { useAvailableAssets } from './useAvailableAssets';
import { useGetAvailableNetworksForAsset } from './useGetAvailableNetworksForAsset';

export type UseDefaultAssetParams = {
  defaultAssetParam: string | undefined;
  defaultNetworkParam: string | undefined;
  destinationWallets: DestinationAddressAppParam[];
  availableAssetsQueryRef: useAvailableAssetsFragment$key;
  getAvailableNetworksQueryRef: useGetAvailableNetworksForAssetFragment$key;
  excludeAssetUuids?: string[]; // These assets will be excluded from the list of available assets.
};

/**
 * Resolves default asset based on destination wallets,
 * the defaultAsset param, assets available on the server,
 * falling back to ETH, and falling back to highest market cap.
 */
export function useDefaultAsset({
  defaultAssetParam,
  defaultNetworkParam,
  destinationWallets,
  availableAssetsQueryRef,
  getAvailableNetworksQueryRef,
  excludeAssetUuids,
}: UseDefaultAssetParams) {
  // 1. Filter assets from server by destination wallets
  // 2. Match defaultAsset param
  // 3. Match first asset of defaultNetwork
  // 4. Match ETH if available & not blocked by blockchains or supportedNetworks of matching wallet
  // 5. Match highest market cap asset

  // 1. Filter assets from server by destination wallets
  const availableAssets = useAvailableAssets({
    destinationWallets,
    queryRef: availableAssetsQueryRef,
    excludeAssetUuids,
  });

  const getAvailableNetworksForAsset = useGetAvailableNetworksForAsset({
    destinationWallets,
    queryRef: getAvailableNetworksQueryRef,
  });

  return useMemo(() => {
    // 2. Match defaultAsset param
    if (defaultAssetParam !== undefined) {
      const defaultParamIsUuid = validateUuid(defaultAssetParam ?? '');
      const defaultAssetByParam = defaultParamIsUuid
        ? availableAssets.find(({ uuid }) => uuid === defaultAssetParam)
        : availableAssets.find(({ symbol }) => symbol === defaultAssetParam);
      if (defaultAssetByParam !== undefined) {
        return defaultAssetByParam;
      }
    }

    // 3. Match first asset of defaultNetworkParam
    if (defaultAssetParam === undefined && defaultNetworkParam !== undefined) {
      const defaultAssetByParam = availableAssets.find(({ uuid }) => {
        const assetNetworks = getAvailableNetworksForAsset(uuid);
        return assetNetworks.some(({ name }) => name === defaultNetworkParam);
      });
      if (defaultAssetByParam !== undefined) {
        return defaultAssetByParam;
      }
    }

    // 4. Match ETH if available & by blockchains or supportedNetworks of matching wallet
    const eth = availableAssets.find(({ symbol }) => symbol === 'ETH');
    if (eth !== undefined) {
      return eth;
    }

    // 5. Match highest market cap asset
    return availableAssets.sort((a, b) => bigJs(b.marketCap).minus(a.marketCap).toNumber())[0];
  }, [availableAssets, defaultAssetParam, defaultNetworkParam, getAvailableNetworksForAsset]);
}
